/**
 * Cover drafted on the skill-gap board, and the one act that makes it real.
 *
 * Drafts are local until Publish: nothing leaves the browser while a planner
 * is still choosing how to cover each short block. Publishing is handed to
 * the caller because it SENDS OFFERS TO REAL WORKERS — the board should not
 * decide on its own that that is safe — and the "Published" state is only
 * set once that caller's request actually succeeded.
 */
import { useCallback, useMemo, useState } from "react";

import { CoverMethod, FlexRequestRow } from "../types";

export interface UseCoverDraftsArgs {
	onPublishDrafts?: (requests: FlexRequestRow[]) => void | Promise<void>;
	/** Called once the batch is published, so the caller can switch screens. */
	onPublished?: () => void;
}

export function useCoverDrafts({ onPublishDrafts, onPublished }: UseCoverDraftsArgs) {
	const [drafts, setDrafts] = useState<Record<string, CoverMethod>>({});
	const [published, setPublished] = useState(false);
	const [publishing, setPublishing] = useState(false);
	const draftCount = Object.keys(drafts).length;

	const draft = useCallback((key: string, method: CoverMethod) => {
		setDrafts((prev) => ({ ...prev, [key]: method }));
	}, []);

	/**
	 * Hand the drafted batch to the caller. Only marks published once the
	 * request succeeded — showing "Published" for a batch that failed would
	 * be worse than showing nothing.
	 */
	const publish = useCallback(
		async (requests: FlexRequestRow[]) => {
			if (draftCount === 0 || published || publishing) {
				return;
			}
			setPublishing(true);
			try {
				await onPublishDrafts?.(requests);
				setPublished(true);
				onPublished?.();
			} finally {
				setPublishing(false);
			}
		},
		[draftCount, published, publishing, onPublishDrafts, onPublished]
	);

	return useMemo(
		() => ({ drafts, draftCount, published, publishing, draft, publish }),
		[drafts, draftCount, published, publishing, draft, publish]
	);
}

export type CoverDrafts = ReturnType<typeof useCoverDrafts>;
